import { LoadingButton } from '@mui/lab'
import { Button, Checkbox, FormControlLabel } from '@mui/material'
import React, { useState } from 'react'
import {
  DEFAULT_DONATION_PERCENTAGE,
  formatCentsInEuros,
  getDonationInCents,
  getDonationInEuros,
  getPaymentFeeContributionInEuros,
  PAYMENT_FEE_COVERAGE_IN_EUROS,
} from '../../domain/GuestAdoption/guestAdoptionDonation'
import { AdoptionDonationEditModal } from '../Adoptions/AdoptionDonationEditModal'
import { AdoptionPriceDetailsModal } from '../Adoptions/AdoptionPriceDetailsModal'
import { InfoBox } from '../common/InfoBox'
import { theme } from '../theme'
import { GuestAdoptionAmountRow, GuestAdoptionCard, GuestAdoptionTitle } from './GuestAdoptionLayout'

interface Props {
  animalName: string
  amountInCents: number
  errorMessage: string | null
  onSubmit: (donationInCents: number) => Promise<void>
  onBack: () => void
}

// Same donation step as the in-app adoption payment (`AdoptionPaymentPath`), with the very same modals.
export const GuestAdoptionDonationStep: React.FC<Props> = ({
  animalName,
  amountInCents,
  errorMessage,
  onSubmit,
  onBack,
}) => {
  const [donation, setDonation] = useState<number>(DEFAULT_DONATION_PERCENTAGE)
  const [coverPaymentFees, setCoverPaymentFees] = useState<boolean>(false)
  const [modalOpened, setModalOpened] = useState<boolean>(false)
  const [donationModalOpened, setDonationModalOpened] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const donationAmount = getDonationInEuros(amountInCents, donation)
  const paymentFeeContribution = getPaymentFeeContributionInEuros(donation, coverPaymentFees)
  const donationInCents = getDonationInCents({ amountInCents, donationPercentage: donation, coverPaymentFees })

  const handleDonationChange = (_: React.MouseEvent<HTMLElement>, newDonation: number | null) => {
    if (newDonation !== null) {
      setDonation(newDonation)
      setCoverPaymentFees(newDonation === 0)
    }
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    await onSubmit(donationInCents)
    setIsLoading(false)
  }

  const linkStyle = { fontStyle: 'normal', fontWeight: 700, color: theme.palette.primary.main, cursor: 'pointer' }

  return (
    <GuestAdoptionCard>
      <GuestAdoptionTitle>Les frais d&apos;adoption :</GuestAdoptionTitle>
      {errorMessage && <InfoBox sx={{ p: 1 }} messageType="error" content={errorMessage} />}
      <GuestAdoptionAmountRow
        label={<>Frais d&apos;adoption de {animalName} :</>}
        amount={formatCentsInEuros(amountInCents)}
      />
      <GuestAdoptionAmountRow
        alignItems="start"
        label={
          <>
            Votre contribution à Petso :
            <br />
            <span style={linkStyle} onClick={() => setModalOpened(true)}>
              Pourquoi ?
            </span>
            <span style={{ fontStyle: 'normal', fontWeight: 700, color: theme.palette.primary.main }}>{` - `}</span>
            <span style={linkStyle} onClick={() => setDonationModalOpened(true)}>
              Modifier
            </span>
          </>
        }
        amount={`${donationAmount}€`}
      />
      {donation === 0 && (
        <GuestAdoptionAmountRow
          label={
            <FormControlLabel
              control={
                <Checkbox
                  checked={coverPaymentFees}
                  onChange={(event) => setCoverPaymentFees(event.target.checked)}
                  color="primary"
                />
              }
              label={`Je participe à hauteur de ${PAYMENT_FEE_COVERAGE_IN_EUROS}€ dans le cadre de cette adoption`}
              sx={{ ml: -1, fontStyle: 'normal' }}
            />
          }
          amount={`${paymentFeeContribution}€`}
        />
      )}
      <GuestAdoptionAmountRow label="Total :" amount={formatCentsInEuros(amountInCents + donationInCents)} />
      <LoadingButton
        variant="contained"
        color="primary"
        fullWidth
        loading={isLoading}
        sx={{ fontSize: '18px', mt: 1.5 }}
        onClick={handleSubmit}
      >
        Accéder au paiement
      </LoadingButton>
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        disabled={isLoading}
        sx={{ fontSize: '18px' }}
        onClick={onBack}
      >
        Retour
      </Button>
      <AdoptionPriceDetailsModal open={modalOpened} handleClose={() => setModalOpened(false)} />
      <AdoptionDonationEditModal
        open={donationModalOpened}
        handleClose={() => setDonationModalOpened(false)}
        donation={donation}
        handleDonationChange={handleDonationChange}
      />
    </GuestAdoptionCard>
  )
}
